/**
 * A table a person can work through. It lives in its own scroll container, so a wide table scrolls
 * inside the page with a shadow on the edge that has more, and the page itself never scrolls
 * sideways. The header stays in view while rows scroll. Below 1024px each row becomes a stacked card
 * that keeps every column, labelled by its header, and a "Sort by" select stands in for the header.
 *
 * The column marked `primary` names the record. When a row has `rowHref` it is a link, and the
 * whole row opens it too. Sortable headers are buttons that say what pressing them will do. `j` and
 * `k` move between rows and Enter opens the focused one.
 *
 * Numbers are right-aligned in tabular figures. A `truncate` column keeps its text to one line and
 * holds the full text in a `title`. Loading shows skeleton rows of the same height, a failed load
 * shows the error with a retry, and no rows shows `empty`.
 */
import * as stylex from "@stylexjs/stylex";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { EmptyState } from "./EmptyState.tsx";
import { ErrorState } from "./ErrorState.tsx";
import { focusRing, focusRingInset } from "./focus.ts";
import { ChevronDownIcon } from "./icons.tsx";
import type { Navigate } from "./Link.tsx";
import { followWith } from "./Link.tsx";
import { Select } from "./Select.tsx";
import { TableRowsSkeleton } from "./Skeleton.tsx";
import { textStyle } from "./textStyle.ts";
const SHADOW = "oklch(0.15 0.03 220 / 0.16)"; // token-exempt: an edge shadow is not a surface colour

const styles = {
  scroll: {
    kVQacm: "xysyzu8 x1h2xkt5",
    kaPefq: "xo2jtoh",
    kskxy: "x12f24lm x1azw9id",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xv9mh3h",
    kaIpWk: "x1fbwzx",
    kWkggS: "x40k5rp",
    $$css: true
  },
  th: {
    kVAEAm: "x7wzq59",
    k87sOh: "x13vifvy",
    kY2c9j: "x1vjfegm",
    kB7OPa: "x9f619",
    k8WAf4: "x1jyo4x1",
    kg3NbH: "xerji9v",
    kWkggS: "x14ba300",
    kt9PQ7: "xq3vdme",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "x10xjq5j",
    kMwMTN: "x1lm7cez",
    k9WMMc: "x1yc453h",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  sortButton: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xskm3c9",
    kAzted: "xv75w1r xei8rr2",
    kmVPX3: "x1717udv",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1heor9g x1fn7bfw",
    kVVagm: "xln7xf2",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  row: {
    k1xSpc: "xf5m44m x1p3q654",
    kWkggS: "xjbqb8w x14uvmqf",
    kt9PQ7: "x1qhh985 xs8e3qw",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "x4a0p42",
    k8WAf4: "xt970qd x1ac5sfl",
    $$css: true
  },
  rowOpen: {
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  td: {
    k1xSpc: "x1n9xxwz x1nuejlj",
    kB7OPa: "x9f619",
    kAzted: "x1schxu6 x158hi1o",
    k8WAf4: "x1jyo4x1",
    kg3NbH: "xerji9v",
    kXLuUW: "xxymvpz",
    kt9PQ7: "xq3vdme x16dntts",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "x4a0p42",
    kMwMTN: "xm8xz4y",
    kOIVth: "xxhr3t xhq2bj2",
    kjj79g: "x12j93s6 xj5ugnh x11h049n",
    kGNEyG: "x13iwyz6 xcr0y23",
    k9WMMc: "x1yc453h x131loxf x1i5zr9m",
    kHjlTd: "x1mzt3pk",
    $$css: true
  },
  label: {
    k1xSpc: "x1s85apg x1p3q654",
    kmuXW: "x2lah0s",
    k7Eaqz: "xeuugli x2hcrpk",
    kMwMTN: "x1lm7cez",
    k9WMMc: "x1yc453h",
    $$css: true
  },
  end: {
    k9WMMc: "xp4054r xbcn7hb",
    $$css: true
  },
  numeric: {
    kcqcaj: "x1l1vl4p",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  cutCell: {
    ks0D6T: "x1m189uc x1g72nx",
    $$css: true
  },
  primaryLink: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kAzted: "xv75w1r xei8rr2",
    kMwMTN: "x1m7e9bc",
    k63SB2: "xqu410z",
    kMnn75: "xkrqix3 x1sur9pj",
    kaIpWk: "x8wwnw1",
    $$css: true
  }
};
export interface Column<Row> {
  readonly id: string;
  readonly header: string;
  readonly cell: (row: Row) => ReactNode;
  /** A CSS width such as "140px". Leave out to share the rest. */
  readonly width?: string;
  readonly minWidth?: string;
  readonly align?: "start" | "end";
  /** Right-aligned in tabular figures, never wrapped. */
  readonly numeric?: boolean;
  /** Keeps the cell to one line. With `text`, the full text is in the cell's `title`. */
  readonly truncate?: boolean;
  /** The cell as plain text, for the `title` of a truncated cell. */
  readonly text?: (row: Row) => string;
  readonly sortable?: boolean;
  /** The column that names the record. It links to the row when the row has an address. */
  readonly primary?: boolean;
}
export interface Sort {
  readonly column: string;
  readonly direction: "asc" | "desc";
}
export type TableState = "ready" | "loading" | "error";
export interface TableProps<Row> {
  /** Names the table for a screen reader: "Pharmacy orders". */
  readonly label: string;
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => string;
  readonly rowHref?: (row: Row) => string | undefined;
  readonly onRowOpen?: (row: Row) => void;
  readonly navigate?: Navigate;
  readonly sort?: Sort | null;
  readonly onSortChange?: (sort: Sort) => void;
  readonly state?: TableState;
  /** Shown instead of the rows when there are none. */
  readonly empty?: ReactNode;
  readonly onRetry?: () => void;
  readonly retrying?: boolean;
  /** Shown instead of `empty` when `filtered`, so the person can clear what hides the rows. */
  readonly noResults?: ReactNode;
  readonly filtered?: boolean;
  readonly errorDetail?: string;
  /** The id a footer's `rowsId` points at. */
  readonly id?: string;
  /** Rows to draw as bones while loading. */
  readonly loadingRows?: number;
  readonly testId?: string;
}
const widthStyles = {
  sized: (width: string, minWidth: string) => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    k7Eaqz: minWidth != null ? "xkj4a21" : minWidth,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width),
    "--x-minWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(minWidth)
  }]
};
function nextDirection(sort: Sort | null | undefined, column: string): "asc" | "desc" {
  return sort?.column === column && sort.direction === "asc" ? "desc" : "asc";
}
function ariaSort(sort: Sort | null | undefined, column: string) {
  if (sort?.column !== column) return "none";
  return sort.direction === "asc" ? "ascending" : "descending";
}
function useEdges() {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({
    start: false,
    end: false
  });
  const measure = () => {
    const node = ref.current;
    if (!node) return;
    const start = node.scrollLeft > 1;
    const end = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    setEdges(was => was.start === start && was.end === end ? was : {
      start,
      end
    });
  };
  useEffect(() => {
    measure();
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    return () => observer.disconnect();
  }, []);
  return {
    ref,
    edges,
    measure
  };
}

/** Whether a click landed on something with its own job, such as a link or button in a cell. */
function onOwnControl(event: MouseEvent): boolean {
  return event.target instanceof Element && event.target.closest("a, button, input, select") !== null;
}
function moveRow(event: KeyboardEvent<HTMLElement>) {
  const step = event.key === "j" ? 1 : event.key === "k" ? -1 : 0;
  if (step === 0 || event.metaKey || event.ctrlKey || event.altKey) return;
  if (event.target instanceof Element && event.target.closest("input, select, textarea")) return;
  const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("tr[data-row]"));
  const at = rows.findIndex(row => row === document.activeElement || row.contains(document.activeElement));
  const next = rows[Math.min(rows.length - 1, Math.max(0, at + step))];
  event.preventDefault();
  next?.focus();
}

/** Whether rows stack as cards (phone and tablet width), where the header is out of sight and "Sort by" does its job. */
function useStacked(): boolean {
  const [phone, setPhone] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 1023px)");
    const change = () => setPhone(query.matches);
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, []);
  return phone;
}
function SortHeader<Row>({
  column,
  sort,
  onSortChange
}: {
  readonly column: Column<Row>;
  readonly sort: Sort | null | undefined;
  readonly onSortChange: ((sort: Sort) => void) | undefined;
}) {
  const next = nextDirection(sort, column.id);
  const current = sort?.column === column.id ? sort.direction : null;
  return <button type="button" aria-label={`${column.header}, sort ${next === "asc" ? "ascending" : "descending"}`} onClick={() => onSortChange?.({
    column: column.id,
    direction: next
  })} {...stylex.props(styles.sortButton, focusRing)}>
      {column.header}
      <span aria-hidden="true" {...{
      0: {
        className: "x3nfvp2 x11xpdln x149xavy"
      },
      2: {
        className: "x3nfvp2 x11xpdln x149xavy x197sbye"
      },
      1: {
        className: "x3nfvp2 x11xpdln x149xavy x19jd1h0"
      },
      3: {
        className: "x3nfvp2 x11xpdln x149xavy x197sbye x19jd1h0"
      }
    }[!!(current === null) << 1 | !!(current === "asc") << 0]}>
        <ChevronDownIcon />
      </span>
    </button>;
}
function Head<Row>({
  columns,
  sort,
  onSortChange
}: {
  readonly columns: readonly Column<Row>[];
  readonly sort: Sort | null | undefined;
  readonly onSortChange: ((sort: Sort) => void) | undefined;
}) {
  const phone = useStacked();
  return <thead role="rowgroup" className="x1uhb9sk x1rl0y27 x14atkfc x168ywsm xt7dq6l xx4ejlt x1rea2x4 x13gj20l x1gj8qfm xctvld4">
      <tr role="row">
        {columns.map(column => <th key={column.id} role="columnheader" scope="col" aria-sort={column.sortable ? ariaSort(sort, column.id) : undefined} {...stylex.props(textStyle.label, styles.th, column.align === "end" || column.numeric ? styles.end : null, column.width !== undefined || column.minWidth !== undefined ? widthStyles.sized(column.width ?? "auto", column.minWidth ?? "0") : null)}>
            {column.sortable && !phone ? <SortHeader column={column} sort={sort} onSortChange={onSortChange} /> : column.header}
          </th>)}
      </tr>
    </thead>;
}
function SortSelect<Row>({
  columns,
  sort,
  onSortChange
}: {
  readonly columns: readonly Column<Row>[];
  readonly sort: Sort | null | undefined;
  readonly onSortChange: ((sort: Sort) => void) | undefined;
}) {
  const options = columns.filter(column => column.sortable === true).flatMap(column => [{
    value: `${column.id}:asc`,
    label: `${column.header}, ascending`
  }, {
    value: `${column.id}:desc`,
    label: `${column.header}, descending`
  }]);
  if (options.length === 0) return null;
  return <div className="x1s85apg x1p3q654 x1o02y6q">
      <Select aria-label="Sort by" placeholder="Sort by" size="md" value={sort ? `${sort.column}:${sort.direction}` : ""} options={options} onChange={value => {
      const [column = "", direction] = value.split(":");
      if (column !== "") onSortChange?.({
        column,
        direction: direction === "desc" ? "desc" : "asc"
      });
    }} />
    </div>;
}
function Rows<Row>({
  columns,
  rows,
  rowKey,
  rowHref,
  onRowOpen,
  navigate
}: Pick<TableProps<Row>, "columns" | "rows" | "rowKey" | "rowHref" | "onRowOpen" | "navigate">) {
  const open = (row: Row) => {
    const href = rowHref?.(row);
    if (href === undefined) onRowOpen?.(row);else if (navigate) navigate(href);else window.location.assign(href);
  };
  return <>
      {rows.map(row => {
      const href = rowHref?.(row);
      const opens = href !== undefined || onRowOpen !== undefined;
      return <tr key={rowKey(row)} data-row="" role="row" tabIndex={-1}
      /* Enter on the row itself opens it. Enter on a link or button inside does that control's job. */ onKeyDown={opens ? event => {
        if (event.key !== "Enter" || event.target !== event.currentTarget) return;
        event.preventDefault();
        open(row);
      } : undefined} onClick={opens ? event => {
        if (!onOwnControl(event)) open(row);
      } : undefined} {...stylex.props(styles.row, opens && styles.rowOpen, focusRingInset)}>
            {columns.map(column => <Cell key={column.id} column={column} row={row} href={column.primary ? href : undefined} navigate={navigate} />)}
          </tr>;
    })}
    </>;
}
function Message({
  children
}: {
  readonly children: ReactNode;
}) {
  return <div className="x1wg0vgl">{children}</div>;
}
export function Table<Row>(props: TableProps<Row>) {
  const {
    label,
    columns,
    rows,
    sort,
    onSortChange,
    state = "ready",
    empty,
    onRetry
  } = props;
  const {
    ref,
    edges,
    measure
  } = useEdges();
  const showRows = state === "ready" && rows.length > 0;
  return <div data-testid={props.testId}>
      <SortSelect columns={columns} sort={sort} onSortChange={onSortChange} />
      <div className="x1n2onr6 xeuugli x193iq5w">
        <div ref={ref} onScroll={measure} onKeyDown={moveRow}
      /* A scroll container is only reachable by keyboard when it can take focus. */ tabIndex={0} role="region" aria-label={label} {...stylex.props(styles.scroll, focusRingInset)}>
          <table id={props.id} role="table" aria-label={label} className="xh8yej3 x1vathgz x1gukg7c xj8fevo x11r6d5e x1p3q654">
            <Head columns={columns} sort={sort} onSortChange={onSortChange} />
            <tbody role="rowgroup" className="x5yl94v x1p3q654">
              {showRows ? <Rows {...props} /> : null}
            </tbody>
          </table>
          {state === "loading" ? <TableRowsSkeleton columns={columns.length} rows={props.loadingRows ?? 5} /> : null}
          {state === "error" ? <Message>
              <ErrorState {...onRetry ? {
            onRetry
          } : {}} retrying={props.retrying ?? false} {...props.errorDetail ? {
            detail: props.errorDetail
          } : {}} />
            </Message> : null}
          {state === "ready" && rows.length === 0 ? <Message>
              {props.filtered && props.noResults ? props.noResults : empty ?? <EmptyState title="Nothing here yet." />}
            </Message> : null}
        </div>
        <span aria-hidden="true" {...{
        0: {
          className: "x10l6tqk x13vifvy x1ey2m1c x1vd0ds5 x47corl xg01cxk x19991ni x149xavy x1o0tod x1byfddg"
        },
        1: {
          className: "x10l6tqk x13vifvy x1ey2m1c x1vd0ds5 x47corl x19991ni x149xavy x1o0tod x1byfddg x1hc1fzr"
        }
      }[!!edges.start << 0]} />
        <span aria-hidden="true" {...{
        0: {
          className: "x10l6tqk x13vifvy x1ey2m1c x1vd0ds5 x47corl xg01cxk x19991ni x149xavy xtijo5x xarim1d"
        },
        1: {
          className: "x10l6tqk x13vifvy x1ey2m1c x1vd0ds5 x47corl x19991ni x149xavy xtijo5x xarim1d x1hc1fzr"
        }
      }[!!edges.end << 0]} />
      </div>
    </div>;
}
function Cell<Row>({
  column,
  row,
  href,
  navigate
}: {
  readonly column: Column<Row>;
  readonly row: Row;
  readonly href: string | undefined;
  readonly navigate: Navigate | undefined;
}) {
  const shown = column.cell(row);
  const missing = shown === null || shown === undefined || shown === "";
  const content = missing ? <span className="xyjz3t7">Not recorded</span> : shown;
  const inner = href === undefined ? content : <a href={href} onClick={followWith(navigate, href)} {...stylex.props(styles.primaryLink, focusRing)}>
        {content}
      </a>;
  return <td role="cell" {...stylex.props(textStyle.bodyCompact, styles.td, column.align === "end" || column.numeric ? styles.end : null, column.numeric && styles.numeric, column.truncate && styles.cutCell)}>
      <span aria-hidden="true" {...stylex.props(textStyle.label, styles.label)}>
        {column.header}
      </span>
      <span title={column.truncate ? column.text?.(row) : undefined} {...{
      0: {
        className: "xeuugli"
      },
      1: {
        className: "x1lliihq xb3r6kr xlyipyv xuxw1ft xr8n7n xeuugli x193iq5w"
      }
    }[!!column.truncate << 0]}>
        {inner}
      </span>
    </td>;
}